import * as React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import {
  Collapsible,
  Field,
  FormBlock,
  Select,
  TextInput,
  type SelectItem,
} from "@/shared/foundation/primitives";
import textStyles from "@/shared/foundation/styles/textStyles.module.css";

import {
  convertWindowValue,
  type WindowInputMode,
} from "../launchAnalysisScope";
import { type LaunchFormValues } from "../launchFormSchema";

import styles from "./sections.module.css";

const MODE_ITEMS: readonly SelectItem<WindowInputMode>[] = [
  { value: "datetime", label: "Date and time" },
  { value: "iso", label: "ISO 8601" },
  { value: "ns", label: "Nanoseconds since epoch" },
];

const MODE_HELP: Record<WindowInputMode, string> = {
  datetime:
    "Limit the agent's analysis to data inside this window, in your local time zone. Leave a bound empty to leave it open.",
  iso: "Limit the agent's analysis to data inside this window, as ISO 8601 timestamps (UTC unless an offset is given). Leave a bound empty to leave it open.",
  ns: "Limit the agent's analysis to data inside this window, in nanoseconds since the Unix epoch. Leave a bound empty to leave it open.",
};

const PLACEHOLDER: Record<WindowInputMode, string | undefined> = {
  datetime: undefined,
  iso: "2026-06-11T12:45:30Z",
  ns: "1781181930000000000",
};

/**
 * Settings most launches leave alone, collapsed until opened: the window of
 * data the agent's analysis is limited to. Switching the window's format
 * converts the bounds already entered rather than clearing them.
 */
export function AdvancedSection() {
  const { control, setValue, getValues } = useFormContext<LaunchFormValues>();
  const analysisWindow = useWatch({ control, name: "window" });
  const windowSet =
    analysisWindow.start.trim() !== "" || analysisWindow.end.trim() !== "";
  const titleId = React.useId();

  const switchMode = (next: WindowInputMode | null) => {
    const current = getValues("window");
    if (next === null || next === current.mode) {
      return;
    }
    const options = { shouldDirty: true };
    setValue(
      "window.start",
      convertWindowValue(current.start, current.mode, next),
      options,
    );
    setValue(
      "window.end",
      convertWindowValue(current.end, current.mode, next),
      options,
    );
    setValue("window.mode", next, options);
  };

  return (
    <Collapsible.Root data-testid="launch-advanced">
      <h2 className={styles.advancedHeading}>
        <Collapsible.Trigger>
          <span className={styles.advancedTitle}>
            Advanced settings
            <span className={styles.advancedHint}>
              Optional settings most threads don&rsquo;t need.
            </span>
          </span>
          {windowSet && (
            <span
              className={styles.windowState}
              data-testid="launch-window-state"
              data-window-set={windowSet}
            >
              Defaults overwritten
            </span>
          )}
        </Collapsible.Trigger>
      </h2>
      <Collapsible.Panel>
        <FormBlock
          titleId={titleId}
          title="Analysis window"
          hint={MODE_HELP[analysisWindow.mode]}
        >
          <div className={styles.windowMode} data-testid="launch-window-mode">
            <Select<WindowInputMode>
              items={MODE_ITEMS}
              value={analysisWindow.mode}
              onValueChange={switchMode}
              aria-label="Analysis window format"
            />
          </div>
          <div className={styles.windowBounds}>
            <WindowBound
              name="window.start"
              label="From"
              mode={analysisWindow.mode}
              testId="launch-window-start"
            />
            <WindowBound
              name="window.end"
              label="To"
              mode={analysisWindow.mode}
              testId="launch-window-end"
            />
          </div>
        </FormBlock>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}

interface WindowBoundProps {
  name: "window.start" | "window.end";
  label: string;
  mode: WindowInputMode;
  testId: string;
}

/** One bound of the window, held as raw text; only the schema parses it. */
function WindowBound({ name, label, mode, testId }: WindowBoundProps) {
  const { control } = useFormContext<LaunchFormValues>();
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <Field.Root name={field.name} invalid={fieldState.invalid}>
          <Field.Label>
            <span className={textStyles.overline}>{label}</span>
          </Field.Label>
          <Field.Control
            render={
              <TextInput
                type={mode === "datetime" ? "datetime-local" : "text"}
                step={mode === "datetime" ? 1 : undefined}
                inputMode={mode === "ns" ? "numeric" : undefined}
                spellCheck={false}
                size="lg"
                data-testid={testId}
              />
            }
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            ref={field.ref}
            placeholder={PLACEHOLDER[mode]}
          />
          <Field.Error match>{fieldState.error?.message}</Field.Error>
        </Field.Root>
      )}
    />
  );
}
